import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useLogin } from '@/hooks/useAuth'
import { useAppStore } from '@/stores/app'
import { usePermissionStore } from '@/stores/permission'
import { AuthEvents } from '@/lib/auth-events'
import { authStorage } from '@/lib/auth-storage'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Loader2,
	User,
	Lock,
	Eye,
	EyeOff
} from 'lucide-react'

// ============================================
// 登录表单 Schema
// ============================================
const loginFormSchema = z.object({
	username: z
		.string()
		.min(1, { message: '请输入用户名' }),
	password: z
		.string()
		.min(1, { message: '请输入密码' }),
	remember: z.boolean().optional(),
})

type LoginFormValues = z.infer<typeof loginFormSchema>

// ============================================
// 登录页面组件
// ============================================
export function LoginPage() {
	const navigate = useNavigate()
	const location = useLocation()
	const [showPassword, setShowPassword] = useState(false)
	const [loginError, setLoginError] = useState<string | null>(null)

	const loginMutation = useLogin()
	const { user: userStore } = useAppStore()

	// 获取 URL 参数
	const searchParams = new URLSearchParams(location.search)
	const redirectParam = searchParams.get('redirect')
	const reasonParam = searchParams.get('reason')

	// 获取重定向地址：优先使用 URL 参数，其次使用 location.state
	interface LocationState {
		from?: { pathname: string }
	}
	const from = redirectParam || (location.state as LocationState)?.from?.pathname || '/dashboard'

	const form = useForm<LoginFormValues>({
		resolver: zodResolver(loginFormSchema),
		defaultValues: {
			username: '',
			password: '',
			remember: false,
		},
	})

	const onSubmit = async (data: LoginFormValues) => {
		setLoginError(null)

		try {
			// 1. 登录获取 token 和用户信息（useLogin hook 已自动存储 token）
			const loginResponse = await loginMutation.mutateAsync({
				username: data.username,
				password: data.password,
			})

			// 2. 获取用户权限，成功后再写入用户态，避免半初始化登录状态
			await usePermissionStore.getState().fetchPermissions()

			// 3. 存储用户信息到 Zustand（使用登录响应中的数据）
			userStore.setUser({
				id: String(loginResponse.user.id),
				username: loginResponse.user.username,
				email: loginResponse.user.email,
				role: loginResponse.user.is_super_admin ? 'admin' : 'operator',
				roleName: loginResponse.role_name || '用户',
				permissions: [], // 权限通过 permission store 获取
				avatar: loginResponse.user.avatar,
				is_super_admin: loginResponse.user.is_super_admin,
				crmeb_uid: loginResponse.user.crmeb_uid,
			})

			// 记住用户名
			if (data.remember) {
				localStorage.setItem('carrot-admin-remembered-username', data.username)
			} else {
				localStorage.removeItem('carrot-admin-remembered-username')
			}

			// 重置 AuthEvents 状态（允许下次 Token 过期时再次触发）
			AuthEvents.reset()

			// 跳转到原页面
			navigate(from, { replace: true })
		} catch (error: unknown) {
			authStorage.removeToken()
			userStore.logout()
			const message = error instanceof Error ? error.message : '登录失败，请重试'
			setLoginError(message)
		}
	}

	return (
		<div className="min-h-screen flex">
			{/* 左侧装饰区域 */}
			<div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-primary/80 relative overflow-hidden">
				{/* 背景装饰 */}
				<div className="absolute inset-0">
					<div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
					<div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
					<div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
				</div>

				{/* 内容 */}
				<div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
					<div className="space-y-6">
						{/* Logo */}
						<div className="flex items-center gap-3">
							<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="h-7 w-7 text-white"
								>
									<path d="M12 2L2 7l10 5 10-5-10-5z" />
									<path d="M2 17l10 5 10-5" />
									<path d="M2 12l10 5 10-5" />
								</svg>
							</div>
							<span className="text-2xl font-bold text-white">Carrot</span>
						</div>

						{/* 标题 */}
						<div className="space-y-4">
							<h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
								电商分销系统
							</h1>
							<p className="text-lg text-white/80 max-w-md">
								一站式电商分销管理平台，助力企业快速构建分销网络，实现业绩增长
							</p>
						</div>

						{/* 特性列表 */}
						<div className="space-y-4 pt-6">
							{[
								{ icon: '📦', title: '商品管理', desc: '多级分销商品体系' },
								{ icon: '👥', title: '用户管理', desc: '完整的用户权限体系' },
								{ icon: '📊', title: '数据分析', desc: '实时数据可视化报表' },
							].map((feature, index) => (
								<div key={index} className="flex items-center gap-4">
									<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur text-xl">
										{feature.icon}
									</div>
									<div>
										<h3 className="font-semibold text-white">{feature.title}</h3>
										<p className="text-sm text-white/60">{feature.desc}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				{/* 底部版权 */}
				<div className="absolute bottom-6 left-12 xl:left-20">
					<p className="text-sm text-white/40">
						© 2024 Carrot. All rights reserved.
					</p>
				</div>
			</div>

			{/* 右侧登录区域 */}
			<div className="flex-1 flex items-center justify-center p-8 bg-background">
				<div className="w-full max-w-md space-y-8">
					{/* 移动端 Logo */}
					<div className="lg:hidden flex items-center justify-center gap-3 mb-8">
						<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="h-6 w-6 text-primary-foreground"
							>
								<path d="M12 2L2 7l10 5 10-5-10-5z" />
								<path d="M2 17l10 5 10-5" />
								<path d="M2 12l10 5 10-5" />
							</svg>
						</div>
						<span className="text-xl font-bold">Carrot</span>
					</div>

					{/* 登录卡片 */}
					<Card className="border-0 shadow-soft-lg">
						<CardHeader className="space-y-1 pb-4">
							<CardTitle className="text-2xl font-bold">登录账户</CardTitle>
							<CardDescription>
								请输入您的账户信息登录系统
							</CardDescription>
						</CardHeader>
						<CardContent>
							<Form {...form}>
								<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
									{/* 全局错误提示 */}
									{loginError && (
										<div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
											<p className="text-sm text-destructive">{loginError}</p>
										</div>
									)}

									{/* Token 过期提示 */}
									{reasonParam === 'expired' && (
										<Alert variant="warning">
											<AlertDescription>登录已过期，请重新登录</AlertDescription>
										</Alert>
									)}

									{/* 用户名 */}
									<FormField
										control={form.control}
										name="username"
										render={({ field }) => (
											<FormItem>
												<FormLabel>用户名</FormLabel>
												<FormControl>
													<div className="relative">
														<User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
														<Input
															placeholder="请输入用户名"
															className="pl-9"
															{...field}
														/>
													</div>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* 密码 */}
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<FormLabel>密码</FormLabel>
												<FormControl>
													<div className="relative">
														<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
														<Input
															type={showPassword ? 'text' : 'password'}
															placeholder="请输入密码"
															className="pl-9 pr-9"
															{...field}
														/>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
															onClick={() => setShowPassword(!showPassword)}
														>
															{showPassword ? (
																<EyeOff className="h-4 w-4 text-muted-foreground" />
															) : (
																<Eye className="h-4 w-4 text-muted-foreground" />
															)}
														</Button>
													</div>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* 记住我 & 忘记密码 */}
									<div className="flex items-center justify-between">
										<FormField
											control={form.control}
											name="remember"
											render={({ field }) => (
												<FormItem className="flex items-center space-x-2 space-y-0">
													<FormControl>
														<Checkbox
															checked={field.value}
															onCheckedChange={field.onChange}
														/>
													</FormControl>
													<FormLabel className="text-sm font-normal cursor-pointer">
														记住我
													</FormLabel>
												</FormItem>
											)}
										/>
									</div>

									{/* 登录按钮 */}
									<Button
										type="submit"
										className="w-full h-10"
										disabled={loginMutation.isPending}
									>
										{loginMutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										登录
									</Button>
								</form>
							</Form>

						</CardContent>
					</Card>

					{/* 底部链接 */}
					<div className="text-center text-sm text-muted-foreground">
						<span>还没有账户？</span>
						<Button variant="link" className="px-1">
							联系管理员
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
